import React, { useContext, useEffect, useState } from "react"
import Button from "../../../ui/Button"
import { AdminContext } from "~/contexts/AdminContext"
import ICoupon from "../../../../interfaces/ICoupon"
import copyText from "../../../../utils/copyText"
import Copy from "../../../../../assets/images/copy_icons/copyOutline.svg?react"
import Copied from "../../../../../assets/images/copy_icons/copiedOutline.svg?react"
import Spinner from "../../../ui/Spinner"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import { convertFloatToPercentageString } from "~/utils/convertFloatToPercentageString"
import parseDateTime from "~/utils/parseDateTime"
import { capitalize } from "lodash"
import CouponsModalContent from "~/components/Admin/Settings/Coupons/CouponsModalContent"
import { formatDate } from "~/utils/admin/formatDate"
import { useCoupons } from "~/api2/hooks/useCoupons"
import { useModal } from "~/contexts/ModalProvider"
import { useCamp } from "~/api2/hooks/useCamp"
import { useNotification } from "~/contexts/NotificationProvider"

const Coupons = () => {
  const { data, isLoading: isLoadingCoupons } = useCoupons()
  const { openModal, closeModal } = useModal()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const coupons = data?.coupons

  const handleShowCouponModal = (isCreate: boolean, coupon?: ICoupon) => {
    openModal("generic", {
      children: <CouponsModalContent couponToEdit={coupon} isCreate={isCreate} setIsLoading={setIsLoading} />,
      onClose: () => {
        closeModal()
      },
      className: "max-w-2xl w-full",
    })
  }

  if (!coupons) return

  return (
    <div className="">
      {(isLoading || isLoadingCoupons) && (
        <div className="fixed bg-black bg-opacity-20 z-40 flex justify-center items-center h-full w-full inset-0">
          <Spinner size={16} />
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-col xs:flex-row">
        <div className="">
          <div className="text-xl font-semibold text-gray-900">Coupons</div>
          <div className="font-medium text-gray-500 max-w-2xl">
            You can use coupons which your guests can apply during checkout to run promotions and share them on social
            media, e-mail newsletters, and more
          </div>
        </div>
        <Button
          variant="blue"
          className=" whitespace-nowrap text-md"
          children={"Add Coupon"}
          onClick={() => handleShowCouponModal(true)}
        />
      </div>
      <CouponTable coupons={coupons} showEditModal={(coupon) => handleShowCouponModal(false, coupon)} />
    </div>
  )
}

const displayDiscountAmount = (coupon: ICoupon, currency: string) => {
  if (typeof coupon.amount_off_in_cents === "number") {
    return (
      <div className="flex flex-wrap">
        <div className="truncate">{formatMoneyFromCents(coupon.amount_off_in_cents, currency)}</div>&nbsp;off per night
      </div>
    )
  } else if (typeof coupon.flat_rate_in_cents === "number") {
    return (
      <div className="flex flex-wrap">
        <div className="truncate">{formatMoneyFromCents(coupon.flat_rate_in_cents, currency)}</div>&nbsp;flat
      </div>
    )
  } else if (typeof coupon.subtotal_amount_off_in_cents === "number") {
    return (
      <div className="flex flex-wrap">
        <div className="truncate">{formatMoneyFromCents(coupon.subtotal_amount_off_in_cents, currency)}</div>&nbsp;off
        subtotal
      </div>
    )
  } else if (coupon.percent_off) {
    return (
      <div className="flex flex-wrap">
        <div className="truncate">{convertFloatToPercentageString(coupon.percent_off)}</div>% off
      </div>
    )
  } else {
    return ""
  }
}

const CouponTable = ({ coupons, showEditModal }: { coupons: ICoupon[]; showEditModal: (coupon: ICoupon) => void }) => {
  const {
    index: { data: camp },
  } = useCamp()
  return (
    <div
      className="overflow-x-auto xl:overflow-visible max-w-full whitespace-nowrap"
      style={{
        maxWidth: "95vw",
      }}
    >
      <div
        style={{
          minWidth: 756,
        }}
      >
        <div className="mt-4 grid overflow-hidden w-full grid-cols-8">
          <div className="py-3 px-2 text-md font-bold border-b border-gray-200 col-span-2">Name</div>
          <div className="py-3 px-2 text-md font-bold border-b border-gray-200">Code</div>
          <div className="py-3 px-2 text-md font-bold border-b border-gray-200">Discount</div>
          <div className="py-3 px-2 text-md font-bold border-b border-gray-200">Expiration by</div>
          <div className="py-3 px-2 text-md font-bold border-b border-gray-200">Valid from</div>
          <div className="py-3 px-2 text-md font-bold border-b border-gray-200">Valid until</div>
          <div className="py-3 px-2 text-md font-bold border-b border-gray-200">Redemptions</div>
          {coupons?.map((coupon, index) => {
            return (
              <React.Fragment key={index}>
                <div className="py-8 px-2 font-semibold border-b border-gray-200 flex items-center col-span-2">
                  {coupon.label}
                </div>
                <div className="py-8 px-2 font-semibold border-b border-gray-200 flex items-center">
                  <CopyButton value={coupon.code} />
                </div>
                <div className="py-8 px-2 font-semibold border-b border-gray-200 text-gray-500 flex items-center">
                  <div className="w-full">{displayDiscountAmount(coupon, camp?.currency || "usd")}</div>
                </div>
                <div className="py-8 px-2 font-semibold border-b border-gray-200 text-gray-500 flex items-center">
                  {parseDateTime(coupon.expiration_by) ? capitalize(coupon.expiration_by).replace("-", " ") : "-"}
                </div>
                <div className="py-8 px-2 font-semibold border-b border-gray-200 text-gray-500 flex items-center">
                  {formatDate(coupon.valid_from, camp)}
                </div>
                <div className="py-8 px-2 font-semibold border-b border-gray-200 text-gray-500 flex items-center">
                  {formatDate(coupon.valid_until, camp)}
                </div>
                <div className="py-8 px-2 border-b border-gray-200 flex justify-between items-center pl-7">
                  <div className="font-semibold text-gray-500">
                    {coupon.number_of_redemptions || 0}
                    {typeof coupon.max_redemptions === "number" ? ` / ${coupon.max_redemptions}` : ""}
                  </div>
                  <Button
                    variant="none"
                    children={"Edit"}
                    className="text-blue-600"
                    style={{ padding: 0 }}
                    onClick={() => showEditModal(coupon)}
                  />
                </div>
              </React.Fragment>
            )
          })}
        </div>
        {coupons?.length < 1 && (
          <div className="w-full flex justify-center py-9 text-md font-semibold text-gray-300">
            No coupons available
          </div>
        )}
      </div>
    </div>
  )
}

const CopyButton = ({ value }: { value: string }) => {
  const [clicked, setClicked] = useState(false)
  const { showNotification } = useNotification()
  const handleCopy = () => {
    copyText(value)
    setClicked(true)
    showNotification({ type: "success", title: "Code copied to clipboard", dismissAfter: 3000, showTimer: true })
  }

  useEffect(() => {
    if (clicked) {
      setTimeout(() => {
        setClicked(false)
      }, 3000)
    }
  }, [clicked])

  return (
    <Button variant="none" className="bg-gray-100 max-w-full !px-2.5" onClick={handleCopy}>
      <div className="flex items-center gap-1">
        <span className="truncate">{value}</span>
        {clicked ? <Copied className="w-6 h-6 !p-0" /> : <Copy className="w-6 h-6 !p-0" />}
      </div>
    </Button>
  )
}

export default Coupons
